import React, { memo } from 'react';

import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import CableOutlinedIcon from '@mui/icons-material/CableOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { noop } from '~/utils/noop';

import type { PropertyRoomTypeWithImages } from '../ImagesVideo/types';
import type { OfferBuilderMediaController } from '../useOfferBuilderMedia';

import { AmenitiesTab, BedsAndSizeTab, ChannelListingTab, DetailsTab, ImagesTab, PoliciesTab } from './RoomTabs';
import type { SyncRoomPolicyParams } from './RoomTabs/PoliciesTab';
import { ROOM_TABS } from './types';
import type { OfferBuilderRoom, RoomTab } from './types';
import { getMissingRequiredRoomFields } from './utils';

interface Props {
  activeRoomTab: RoomTab;
  expanded: boolean;
  imageCount: number;
  media: OfferBuilderMediaController;
  onChange: (nextRoomValues: Partial<OfferBuilderRoom>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onExpandedChange: (isExpanded: boolean) => void;
  onOpenImagesVideo: () => void;
  onTabChange: (tab: RoomTab) => void;
  propertyName: string;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  room: OfferBuilderRoom;
  roomKey: string;
  roomSummary: string;
  roomTitle: string;
  syncBeddingConfigurationPackageId?: string;
  syncRoomPolicyParams?: SyncRoomPolicyParams;
}

function RoomAccordion({
  activeRoomTab,
  expanded,
  imageCount,
  media,
  onChange,
  onDelete,
  onDuplicate,
  onExpandedChange,
  onOpenImagesVideo,
  onTabChange,
  propertyName,
  propertyRoomTypes,
  room,
  roomKey,
  roomSummary,
  roomTitle,
  syncBeddingConfigurationPackageId,
  syncRoomPolicyParams,
}: Props) {
  const missingRequiredFields = getMissingRequiredRoomFields(room);
  const hasMissingRequiredFields = missingRequiredFields.length > 0;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_event, isExpanded) => onExpandedChange(isExpanded)}
      sx={{ bgcolor: 'background.paper' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ width: '100%', pr: 2 }}
        >
          <Box>
            <Typography variant="h6">{roomTitle || 'Unnamed room'}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
              <Chip size="small" variant="outlined" label={roomSummary} />
              {missingRequiredFields.map((field) => (
                <Chip
                  key={field.key}
                  size="small"
                  color="warning"
                  icon={<ErrorOutlineIcon />}
                  label={`${field.label} missing`}
                />
              ))}
            </Stack>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {imageCount} {imageCount === 1 ? 'image' : 'images'}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {hasMissingRequiredFields && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {formatMissingRequiredFields(missingRequiredFields)} required before saving this room.
          </Alert>
        )}

        <Tabs
          value={activeRoomTab}
          onChange={(_event, value) => onTabChange(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          {ROOM_TABS.map((tab) => (
            <Tab key={tab} value={tab} label={tab} icon={getRoomTabIcon(tab)} iconPosition="start" />
          ))}
        </Tabs>

        {activeRoomTab === 'Details' && <DetailsTab propertyName={propertyName} room={room} onChange={onChange} />}
        {activeRoomTab === 'Beds & size' && (
          <BedsAndSizeTab
            room={room}
            syncBeddingConfigurationPackageId={syncBeddingConfigurationPackageId}
            onChange={onChange}
          />
        )}
        {activeRoomTab === 'Amenities' && <AmenitiesTab room={room} onChange={onChange} />}
        {activeRoomTab === 'Channel & listing' && <ChannelListingTab room={room} onChange={onChange} />}
        {activeRoomTab === 'Images' && (
          <ImagesTab
            media={media}
            onOpenImagesVideo={onOpenImagesVideo}
            propertyRoomTypes={propertyRoomTypes ?? []}
            roomKey={roomKey}
          />
        )}
        {activeRoomTab === 'Policies' && (
          <PoliciesTab room={room} syncRoomPolicyParams={syncRoomPolicyParams} onChange={onChange} />
        )}

        <Divider sx={{ my: 3 }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
          <Button variant="text" color="error" onClick={onDelete}>
            Delete room
          </Button>
          <Stack direction="row" spacing={1}>
            <Button variant="text" onClick={onDuplicate}>
              Duplicate
            </Button>
            <Button variant="contained" disabled={hasMissingRequiredFields} onClick={noop}>
              Save room
            </Button>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function formatMissingRequiredFields(fields: ReturnType<typeof getMissingRequiredRoomFields>) {
  const labels = fields.map((field) => field.label);

  if (labels.length === 1) {
    return `${fields[0].label} is`;
  }

  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]} are`;
}

function getRoomTabIcon(tab: RoomTab) {
  switch (tab) {
    case 'Details':
      return <DescriptionOutlinedIcon />;
    case 'Beds & size':
      return <BedOutlinedIcon />;
    case 'Amenities':
      return <SpaOutlinedIcon />;
    case 'Channel & listing':
      return <CableOutlinedIcon />;
    case 'Images':
      return <ImageOutlinedIcon />;
    case 'Policies':
      return <GavelOutlinedIcon />;
    default:
      return undefined;
  }
}

export default memo(RoomAccordion, areRoomAccordionPropsEqual);

function areRoomAccordionPropsEqual(previousProps: Props, nextProps: Props) {
  return (
    previousProps.activeRoomTab === nextProps.activeRoomTab &&
    previousProps.expanded === nextProps.expanded &&
    previousProps.imageCount === nextProps.imageCount &&
    previousProps.media === nextProps.media &&
    previousProps.propertyName === nextProps.propertyName &&
    previousProps.propertyRoomTypes === nextProps.propertyRoomTypes &&
    previousProps.room === nextProps.room &&
    previousProps.roomKey === nextProps.roomKey &&
    previousProps.roomSummary === nextProps.roomSummary &&
    previousProps.roomTitle === nextProps.roomTitle &&
    previousProps.syncBeddingConfigurationPackageId === nextProps.syncBeddingConfigurationPackageId &&
    areSyncRoomPolicyParamsEqual(previousProps.syncRoomPolicyParams, nextProps.syncRoomPolicyParams)
  );
}

function areSyncRoomPolicyParamsEqual(
  previousParams: SyncRoomPolicyParams | undefined,
  nextParams: SyncRoomPolicyParams | undefined,
) {
  return (
    previousParams?.dealOfferId === nextParams?.dealOfferId &&
    previousParams?.propertyId === nextParams?.propertyId &&
    previousParams?.roomTypeId === nextParams?.roomTypeId &&
    previousParams?.roomRateId === nextParams?.roomRateId &&
    areRoomRateIdsEqual(previousParams?.roomRateIds, nextParams?.roomRateIds)
  );
}

function areRoomRateIdsEqual(previousRoomRateIds?: Array<string>, nextRoomRateIds?: Array<string>) {
  if (previousRoomRateIds === nextRoomRateIds) {
    return true;
  }

  if (previousRoomRateIds?.length !== nextRoomRateIds?.length) {
    return false;
  }

  return previousRoomRateIds?.every((roomRateId, index) => roomRateId === nextRoomRateIds?.[index]) ?? false;
}
